import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Printer, Building2, BookOpen, Book, NotebookPen, Layers, CircleDot, Zap } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const iconMap = {
  'commercial-printing': Printer,
  'government-contracts': Building2,
  'magazine-printing': BookOpen,
  'book-printing': Book,
  'notebook-printing': NotebookPen,
  'binding-finishing': Layers,
  'offset-printing': CircleDot,
  'web-printing': Zap
};

const Services = () => {
  const [services, setServices] = useState([]);
  const [hoveredIndex, setHoveredIndex] = useState(null);

  useEffect(() => {
    const fetchServices = async () => {
      try {
        const response = await axios.get(`${API}/services`);
        setServices(response.data);
      } catch (error) {
        console.error('Error fetching services:', error);
      }
    };
    fetchServices();
  }, []);

  return (
    <section id="services" className="bg-gray-50 section-padding" data-testid="services-section" aria-labelledby="services-heading">
      <div className="container-custom">
        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto mb-12">
          <p className="overline mb-4" data-testid="services-overline">
            Our Services
          </p>
          <h2 id="services-heading" className="text-2xl sm:text-3xl lg:text-4xl font-extrabold tracking-tight leading-tight font-heading text-[#1a1a1a] mb-4" data-testid="services-heading">
            Complete Printing Solutions
          </h2>
          <p className="text-gray-600 leading-relaxed">
            From commercial printing to government contracts, we deliver quality and precision at every scale. 
            ISO certified processes ensuring excellence in every print.
          </p>
        </div>

        {/* Services Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="services-grid">
          {services.map((service, index) => {
            const IconComponent = iconMap[service.id] || Printer;
            return (
              <article
                key={service.id}
                className={`p-6 rounded-lg transition-all cursor-pointer ${
                  hoveredIndex === index 
                    ? 'bg-[#003151] text-white transform -translate-y-1' 
                    : 'bg-white border border-gray-100 hover:border-[#003151]/20'
                }`}
                onMouseEnter={() => setHoveredIndex(index)}
                onMouseLeave={() => setHoveredIndex(null)}
                data-testid={`service-card-${index}`}
                itemScope
                itemType="https://schema.org/Service"
              >
                <div className={`inline-flex p-3 rounded-lg mb-4 ${hoveredIndex === index ? 'bg-white/10' : 'bg-[#003151]/10'}`}>
                  <IconComponent className={`w-6 h-6 ${hoveredIndex === index ? 'text-white' : 'text-[#003151]'}`} aria-hidden="true" />
                </div>
                
                <div className="flex items-center justify-between mb-2">
                  <h3
                    className="text-lg font-bold font-heading"
                    data-testid={`service-title-${index}`}
                    itemProp="name"
                  >
                    {service.title}
                  </h3>
                  <span className={`text-xs font-medium ${hoveredIndex === index ? 'text-white/60' : 'text-gray-400'}`}>
                    {service.index}
                  </span>
                </div>
                
                <p
                  className={`text-sm leading-relaxed ${
                    hoveredIndex === index ? 'text-white/80' : 'text-gray-600'
                  }`}
                  data-testid={`service-description-${index}`}
                  itemProp="description"
                >
                  {service.description}
                </p>
              </article>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default Services;
